import { useEffect, useMemo, useRef } from 'react';

import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { Routes, QUOTE_LABEL, WEEKS_SESSION_KEY_LABEL, WEEKS_UNITS_SESSION_KEY_LABEL } from '@share/constants';
import { getWeeksReviewBook, IWeeksReviewBookState, weeksReviewBookAction } from '@store/slices';
import { ICondosStaticState } from '@share/store/slices';
import { BookingErrorsEnum, ISessionKey } from '@share/common-types';
import {
  getSessionObj,
  getTimeout,
  UrlUtils,
  RootState,
  computeCreditLineChargeTotal,
  getSelectedCurrency,
  isCreditLineCheckoutAccount,
} from '@share/utils';
import { CreditLineCheckoutPageBanner } from '@share/components';
import { IInsuranceState, InsuranceSelection } from '@store/slices';
import { Notification, SkeletonReviewBook } from '@components';
import { WeeksReviewBook } from '../weeks-review-book';
import { WeeksUnitInfo } from '../weeks-unit-info';
import { ILoginState } from '@share/store/slices';
import { NULL_VALUE } from '@constants';

import './style.scss';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IMapStateToProps {
  weeksReviewBookStore: IWeeksReviewBookState;
  condosStaticStore: ICondosStaticState;
  loginStore: ILoginState;
  insuranceStore: IInsuranceState;
}

interface IMapDispatchToProps {
  resetBooking: () => void;
  getWeeksReviewBook: (
    numberId: number,
    packageId: string,
    sessionKey: ISessionKey,
    isGetCoupon?: false,
    quote?: string
  ) => void;
  setLoadingWeeksBook: (loadingBook: boolean) => void;
  setWeeksExpiredSession: (expiredSession: boolean) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps {}

const ZERO = 0;
const FOUR = 4;
const FIVE = 5;

function WeeksReviewBookWrapperComponent(props: IProps) {
  
  const { history, weeksReviewBookStore, condosStaticStore, resetBooking, getWeeksReviewBook, insuranceStore, loginStore } = props;
  const {
    weeksBookingSummary,
    loading,
    error,
    expiredSession,
    isExternalError,
    bookingComplete,
    isBookingPending,
    bookingErrorCode,
    loadingBooking,
    isSavingsMismatch,
  } = weeksReviewBookStore;
  const { condosCountriesCode } = condosStaticStore;
  const { account, userWalletData } = loginStore;
  const { selection, insurance } = insuranceStore;
  const { selectedWeeksReviewClientCash, rewardId } = weeksReviewBookStore;

  const historySplit = useMemo(() => history.location.pathname.split('/'), [history.location.pathname]);
  const propertyId = Number(historySplit[FOUR]);
  const packageId = historySplit[FIVE];

  const loadingRef = useRef(loading);
  const errorRef = useRef(error);
  
  const getSessionId = () => {
    const values = UrlUtils.getValues();
    return getSessionObj(
      values[WEEKS_UNITS_SESSION_KEY_LABEL] as ISessionKey,
      values[WEEKS_SESSION_KEY_LABEL] as ISessionKey,
    );
  }

  useEffect(() => {
    const values = UrlUtils.getValues();
    const sessionKey = getSessionId();

    let quote;
    if (values[QUOTE_LABEL]) {
      quote = (values[QUOTE_LABEL] as string);
    }

    getWeeksReviewBook(propertyId, packageId, sessionKey, false, quote);
    onExpiredSession(sessionKey);
    window.scrollTo(ZERO, ZERO);

    return () => {
      resetBooking();
    }
  },[]);

  useEffect(() => {
    if (loadingRef.current !== loading && !loading && errorRef.current === BookingErrorsEnum.BookingSummeryInformationFailed) {
      history.push(`/${account?.name}${Routes.WeeksDetails}/${propertyId}${location.search}`);
      location.reload();
    }
    loadingRef.current = loading;
  },[loading]);

  useEffect(() => {
    errorRef.current = error;
  },[error]);

  const onExpiredSession = (sessionData: ISessionKey) => {
    const expireDate = getTimeout(sessionData.expireDate);
    const timeout = expireDate < ZERO ? 0 : expireDate;

    setTimeout(() => {
      props.setWeeksExpiredSession(true);
    }, timeout);
  };

  const isNotification = bookingComplete || loadingBooking || isBookingPending || bookingErrorCode === BookingErrorsEnum.SoldOut;
  const isUnavailable = bookingErrorCode === BookingErrorsEnum.SoldOut || bookingErrorCode === BookingErrorsEnum.RoomsUnavailable;
  const displayNotification = expiredSession || isExternalError || !condosCountriesCode || isUnavailable;
  const sessionKey = getSessionId();

  const { payNowPrice, maxRewardWalletAmount } = weeksBookingSummary?.bookingCard?.bookingPrice ?? {};
  const convertionRate = userWalletData?.convertionRate ?? 1;
  const insuranceAmount =
    selection === InsuranceSelection.YES && insurance?.productDetails?.price
      ? insurance.productDetails.price
      : 0;
  const rewardDeduction = rewardId && maxRewardWalletAmount ? maxRewardWalletAmount : 0;
  const creditLineBookingAmount =
    payNowPrice != null
      ? computeCreditLineChargeTotal({
          payOrBalance: payNowPrice,
          clientCashPoints: selectedWeeksReviewClientCash,
          conversionRate: convertionRate,
          insuranceAmount,
          rewardDeduction,
        })
      : 0;
  const showCreditLineBanner =
    isCreditLineCheckoutAccount(account) &&
    !isNotification &&
    !!weeksBookingSummary &&
    !loading &&
    !isUnavailable;

  return displayNotification ? (
    <Notification
      expiredSession={expiredSession}
      isExternalError={isExternalError || !condosCountriesCode}
      isFromCondo={true}
      isSavingsMismatch={isSavingsMismatch}
      errorCode={isUnavailable ? bookingErrorCode : NULL_VALUE}
      sessionId={sessionKey?.value || ''}
    />) : (
      <div className="review-book-wrapper print">
      {!isNotification && (
        <div className="review-book-wrapper__header print">
          <div className="review-book-wrapper__back-link">
            <Link to={`/${account?.name}${Routes.WeeksDetails}/${propertyId}${location.search}`}>
              <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '5px' }} />
              <FormattedMessage id="back.to.condo" />
            </Link>
          </div>
          <h4 className="review-book-wrapper__title secondary-font">
            <FormattedMessage id="review.book" />
          </h4>
          <p className="review-book-wrapper__description">
            <FormattedMessage id="enter.your.details" />
          </p>
        </div>
      )}

      {showCreditLineBanner ? (
        <CreditLineCheckoutPageBanner
          bookingAmount={creditLineBookingAmount}
          currency={getSelectedCurrency(account)}
        />
      ) : null}
      
      {loading ? <SkeletonReviewBook /> : null}
      {weeksBookingSummary && !loading && condosCountriesCode ? (
        <div className="review-book-wrapper__content">
          <WeeksReviewBook />
          <WeeksUnitInfo />
        </div>
      ) : null}
      </div>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    weeksReviewBookStore: state.weeksReviewBookStore,

    condosStaticStore: state.condosStaticStore,
    loginStore: state.loginStore,
    insuranceStore: state.insuranceStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  getWeeksReviewBook,
  setLoadingWeeksBook: weeksReviewBookAction.setLoadingWeeksBook,
  setWeeksExpiredSession: weeksReviewBookAction.setWeeksExpiredSession,
  resetBooking: weeksReviewBookAction.weekBookingResetState,
};

export const WeeksReviewBookWrapper = connect(mapStateToProps, mapDispatchToProps)(withRouter(WeeksReviewBookWrapperComponent));
